import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    Button,
    Card,
    CardBody,
    CardHeader,
    Skeleton,
} from "@heroui/react";
import {
    Download,
    Eye,
    FileText,
    Calendar,
    AlertCircle,
    BookOpen,
    GraduationCap,
    Hash,
    Building2,
    ScrollText,
} from "lucide-react";
import {
    useGetPascoById,
    useGetPascoPreviewUrl,
    useDownloadPascoStream,
} from "@src/presentation/hooks/pasco";
import { ToastService } from "@src/shared/services/toast.service";
import { formatDateHuman } from "@src/shared/utils/date-formatter";
import { useAuth } from "@src/presentation/contexts/AuthContext";
import useScreenSize, { ScreenSize } from "@src/presentation/hooks/useScreenSize";

interface PascoDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    pascoId: string | null;
}

export function PascoDetailModal({ isOpen, onClose, pascoId }: PascoDetailModalProps) {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const screenSize = useScreenSize();
    const isMobile = screenSize === ScreenSize.Mobile;

    // Fetch PASCO details only if authenticated
    const { data: pasco, isLoading, error } = useGetPascoById(pascoId || "", {
        enabled: isAuthenticated && !!pascoId, // Only fetch if authenticated and has ID
    });

    // Fetch preview URL for JPG preview (auto-fetch once PASCO details are loaded)
    const {
        data: previewUrl,
        isLoading: isLoadingPreview,
    } = useGetPascoPreviewUrl(pascoId || undefined, {
        enabled: !!pascoId && !!pasco && isAuthenticated, // Fetch when we have PASCO ID, PASCO details are loaded, and user is authenticated
    });

    // Download mutation for streaming the file
    const { mutate: downloadPascoStream, isPending: isDownloading } = useDownloadPascoStream({
        onSuccess: () => {
            ToastService.success("Download started successfully");
        },
        onError: (error) => {
            ToastService.error(`Failed to download PASCO: ${error.message}`);
        }
    });

    const handleDownload = async () => {
        // Ensure user is authenticated before allowing download
        if (!isAuthenticated) {
            ToastService.error("You must be logged in to download this file.");
            onClose(); // Close the modal
            navigate("/auth", { state: { returnUrl: window.location.pathname } });
            return;
        }

        if (!pascoId || !pasco) return;

        // Generate filename based on PASCO details
        const filename = `${pasco.courseCode}_${pasco.semester}_${pasco.academicYear}.pdf`.replace(/\s+/g, '_');

        // Use the new stream endpoint
        downloadPascoStream({ pascoId, filename });
    };

    // Handle mobile back button to close modal instead of navigating
    useEffect(() => {
        if (!isOpen) return;

        // Push a new state when modal opens
        window.history.pushState({ modal: true }, "");

        const handlePopState = () => {
            // Close the modal when back button is pressed
            onClose();
        };

        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);
            // Remove the pushed state if modal is still open and we're closing it programmatically
            if (isOpen && window.history.state?.modal) {
                window.history.back();
            }
        };
    }, [isOpen, onClose]);

    // If not authenticated, show login prompt
    if (!isAuthenticated) {
        return (
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                size={isMobile ? "full" : "4xl"}
                scrollBehavior="inside"
                classNames={{
                    base: isMobile
                        ? "m-0 rounded-none h-full max-h-full"
                        : "rounded",
                    body: isMobile
                        ? "p-0 h-full"
                        : "p-6",
                    closeButton: isMobile
                        ? "top-4 right-4 z-50 bg-default-100 hover:bg-default-200 text-foreground shadow-lg"
                        : "top-4 right-4 bg-default-100 hover:bg-default-200",
                }}
                backdrop={isMobile ? "blur" : "opaque"}
            >
                <ModalContent>
                    <ModalBody className="flex items-center justify-center min-h-full">
                        <Card className="max-w-md w-full shadow-none border border-border rounded">
                            <CardBody className="text-center p-8">
                                <div className="flex justify-center mb-4">
                                    <div className="p-3 rounded-full bg-primary/10">
                                        <FileText className="h-12 w-12 text-primary" />
                                    </div>
                                </div>

                                <h2 className="text-xl font-semibold text-foreground mb-2">
                                    Login Required
                                </h2>

                                <p className="text-default-500 mb-6">
                                    This PASCO document requires authentication. Please sign in to view.
                                </p>

                                <div className="flex flex-col gap-2">
                                    <Button
                                        color="primary"
                                        onPress={() => {
                                            // Navigate to login with return path
                                            onClose();
                                            navigate("/auth", { state: { returnUrl: window.location.pathname } });
                                        }}
                                        startContent={<FileText className="h-4 w-4" />}
                                        className="rounded"
                                    >
                                        Login to View
                                    </Button>
                                    <Button
                                        variant="flat"
                                        onPress={onClose}
                                        className="rounded"
                                    >
                                        Close
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>
                    </ModalBody>
                </ModalContent>
            </Modal>
        );
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size={isMobile ? "full" : "5xl"}
            scrollBehavior="inside"
            classNames={{
                base: isMobile
                    ? "m-0 rounded-none h-full max-h-full"
                    : "rounded max-h-[90vh]",
                body: isMobile
                    ? "p-4 h-full overflow-hidden"
                    : "p-6 max-h-[80vh] overflow-hidden",
                header: isMobile
                    ? "border-b border-border p-4"
                    : "border-b border-border p-6",
                closeButton: isMobile
                    ? "top-4 right-4 z-50 bg-default-100 hover:bg-default-200 text-foreground shadow-lg"
                    : "top-4 right-4 bg-default-100 hover:bg-default-200",
            }}
            backdrop={isMobile ? "blur" : "opaque"}
        >
            <ModalContent>
                {isLoading ? (
                    // Loading state
                    <>
                        <ModalHeader className="flex items-center gap-3">
                            <Skeleton className="h-8 w-8 rounded-full" />
                            <div className="space-y-2">
                                <Skeleton className="h-5 w-48" />
                                <Skeleton className="h-4 w-24" />
                            </div>
                        </ModalHeader>
                        <ModalBody className="overflow-y-auto pb-8">
                            <div className={`grid grid-cols-1 ${isMobile ? 'gap-4' : 'lg:grid-cols-3 gap-6'} h-full`}>
                                {/* PDF Viewer */}
                                <div className={isMobile ? 'col-span-1' : 'lg:col-span-2'}>
                                    <Card className="shadow-none border border-border rounded hover:shadow-custom transition-shadow">
                                        <CardHeader className="flex flex-row items-center justify-between border-b border-divider pb-4">
                                            <div className="flex items-center gap-2">
                                                <div className="p-2 rounded-lg bg-primary/10">
                                                    <Skeleton className="h-5 w-5 rounded" />
                                                </div>
                                                <Skeleton className="h-5 w-32" />
                                            </div>
                                        </CardHeader>
                                        <CardBody className="p-0">
                                            <div className="bg-default-50 rounded-b-xl overflow-hidden">
                                                <div className={`w-full ${isMobile ? 'h-96' : 'h-[calc(70vh-8rem)]'} rounded p-4`}>
                                                    <Skeleton className="w-full h-full rounded" />
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>

                                {/* Metadata */}
                                <div className="space-y-4">
                                    {/* Course Information */}
                                    <Card className="shadow-none border border-border rounded hover:shadow-custom transition-shadow">
                                        <CardHeader className="border-b border-divider pb-3">
                                            <Skeleton className="h-4 w-32" />
                                        </CardHeader>
                                        <CardBody className="space-y-3">
                                            <div className="flex items-start gap-3">
                                                <div className="p-2 rounded-lg bg-primary/10">
                                                    <Skeleton className="h-4 w-4 rounded" />
                                                </div>
                                                <div className="flex-1 space-y-1">
                                                    <Skeleton className="h-3 w-20" />
                                                    <Skeleton className="h-4 w-24" />
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-3">
                                                <div className="p-2 rounded-lg bg-secondary/10">
                                                    <Skeleton className="h-4 w-4 rounded" />
                                                </div>
                                                <div className="flex-1 space-y-1">
                                                    <Skeleton className="h-3 w-24" />
                                                    <Skeleton className="h-4 w-32" />
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-3">
                                                <div className="p-2 rounded-lg bg-warning/10">
                                                    <Skeleton className="h-4 w-4 rounded" />
                                                </div>
                                                <div className="flex-1 space-y-1">
                                                    <Skeleton className="h-3 w-16" />
                                                    <Skeleton className="h-4 w-28" />
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-3">
                                                <div className="p-2 rounded-lg bg-success/10">
                                                    <Skeleton className="h-4 w-4 rounded" />
                                                </div>
                                                <div className="flex-1 space-y-1">
                                                    <Skeleton className="h-3 w-12" />
                                                    <Skeleton className="h-4 w-20" />
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>

                                    {/* Academic Details */}
                                    <Card className="shadow-none border border-border rounded hover:shadow-custom transition-shadow">
                                        <CardHeader className="border-b border-divider pb-3">
                                            <Skeleton className="h-4 w-28" />
                                        </CardHeader>
                                        <CardBody className="space-y-3">
                                            <div className="flex items-start gap-3">
                                                <div className="p-2 rounded-lg bg-blue-500/10">
                                                    <Skeleton className="h-4 w-4 rounded" />
                                                </div>
                                                <div className="flex-1 space-y-1">
                                                    <Skeleton className="h-3 w-28" />
                                                    <Skeleton className="h-4 w-16" />
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-3">
                                                <div className="p-2 rounded-lg bg-purple-500/10">
                                                    <Skeleton className="h-4 w-4 rounded" />
                                                </div>
                                                <div className="flex-1 space-y-1">
                                                    <Skeleton className="h-3 w-16" />
                                                    <Skeleton className="h-4 w-20" />
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-3">
                                                <div className="p-2 rounded-lg bg-green-500/10">
                                                    <Skeleton className="h-4 w-4 rounded" />
                                                </div>
                                                <div className="flex-1 space-y-1">
                                                    <Skeleton className="h-3 w-12" />
                                                    <Skeleton className="h-4 w-24" />
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>
                            </div>
                        </ModalBody>
                    </>
                ) : error || !pasco ? (
                    // Error state
                    <>
                        <ModalHeader>
                            <div className="flex items-center gap-2">
                                <AlertCircle className="h-5 w-5 text-danger" />
                                PASCO Not Found
                            </div>
                        </ModalHeader>
                        <ModalBody className="flex items-center justify-center py-12">
                            <div className="text-center">
                                <AlertCircle className="h-12 w-12 text-danger mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-foreground mb-2">PASCO Not Found</h3>
                                <p className="text-default-500 mb-6">
                                    {error?.message || "The PASCO you're looking for doesn't exist or has been removed."}
                                </p>
                                <Button color="primary" onPress={onClose} className="rounded">
                                    Close
                                </Button>
                            </div>
                        </ModalBody>
                    </>
                ) : (
                    // Main content
                    <>
                        <ModalHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-3 flex-1 min-w-0 pr-12">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-600 shadow-md flex-shrink-0">
                                    <FileText className="h-4 w-4 text-primary-foreground" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h1 className={`font-semibold text-foreground ${isMobile ? 'text-base line-clamp-2' : 'text-lg truncate'}`}>
                                        {pasco.courseCode} - {pasco.courseName}
                                    </h1>
                                    <p className="text-sm text-default-500">{pasco.academicYear}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 w-full sm:w-auto flex-shrink-0">
                                <Button
                                    variant="flat"
                                    color="default"
                                    onPress={handleDownload}
                                    startContent={<Download className="h-4 w-4" />}
                                    className="rounded flex-1 sm:flex-initial"
                                    isLoading={isDownloading}
                                    size="sm"
                                >
                                    Download PDF
                                </Button>
                            </div>
                        </ModalHeader>

                        <ModalBody className="overflow-y-auto pb-8">
                            <div className={`grid grid-cols-1 ${isMobile ? 'gap-4' : 'lg:grid-cols-3 gap-6'} h-full`}>
                                {/* PDF Viewer */}
                                <div className={isMobile ? 'col-span-1' : 'lg:col-span-2'}>
                                    <Card className="shadow-none border border-border rounded hover:shadow-custom transition-shadow">
                                        <CardHeader className="flex flex-row items-center justify-between border-b border-divider pb-4">
                                            <div className="flex items-center gap-2">
                                                <div className="p-2 rounded-lg bg-primary/10">
                                                    <Eye className="h-5 w-5 text-primary" />
                                                </div>
                                                <h2 className="text-lg font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                                    PDF Preview
                                                </h2>
                                            </div>
                                        </CardHeader>
                                        <CardBody className="p-0">
                                            <div className="bg-default-50 rounded overflow-hidden">
                                                {isLoadingPreview ? (
                                                    <div className={`w-full ${isMobile ? 'h-96' : 'h-[calc(70vh-8rem)]'} rounded p-4`}>
                                                        <Skeleton className="w-full h-full rounded" />
                                                    </div>
                                                ) : previewUrl ? (
                                                    <div className={`w-full ${isMobile ? 'h-96' : 'h-[calc(70vh-8rem)]'} overflow-y-hidden overflow-x-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-default-100 [&::-webkit-scrollbar-thumb]:bg-default-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-default-400`}>
                                                        <img
                                                            src={previewUrl.previewUrl}
                                                            alt={`${pasco.courseCode} Preview`}
                                                            className="w-full h-auto object-contain"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className={`w-full ${isMobile ? 'h-96' : 'h-[calc(70vh-8rem)]'} rounded flex items-center justify-center`}>
                                                        <div className="text-center">
                                                            <AlertCircle className="h-12 w-12 text-warning mx-auto mb-4" />
                                                            <p className="text-default-600">
                                                                Unable to load preview. Please use the Download button above.
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>

                                {/* Metadata */}
                                <div className="space-y-4 pb-8">
                                    {/* Course Information */}
                                    <Card className="shadow-none border border-border rounded hover:shadow-custom transition-shadow">
                                        <CardHeader className="border-b border-divider pb-3">
                                            <h3 className="text-sm font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                                Course Information
                                            </h3>
                                        </CardHeader>
                                        <CardBody className="space-y-3">
                                            <div className="flex items-start gap-3">
                                                <div className="p-2 rounded-lg bg-primary/10">
                                                    <BookOpen className="h-4 w-4 text-primary" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Course Code</p>
                                                    <p className="text-sm font-semibold text-foreground">{pasco.courseCode}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-3">
                                                <div className="p-2 rounded-lg bg-secondary/10">
                                                    <FileText className="h-4 w-4 text-secondary" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Course Name</p>
                                                    <p className="text-sm font-semibold text-foreground">{pasco.courseName}</p>
                                                </div>
                                            </div>
                                            {pasco.department && (
                                                <div className="flex items-start gap-3">
                                                    <div className="p-2 rounded-lg bg-warning/10">
                                                        <Building2 className="h-4 w-4 text-warning" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Department</p>
                                                        <p className="text-sm font-semibold text-foreground">{pasco.department}</p>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="flex items-start gap-3">
                                                <div className="p-2 rounded-lg bg-success/10">
                                                    <GraduationCap className="h-4 w-4 text-success" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Level</p>
                                                    <p className="text-sm font-semibold text-foreground">Level {pasco.level}</p>
                                                </div>
                                            </div>
                                            {pasco.courseDescription && (
                                                <div className="flex items-start gap-3">
                                                    <div className="p-2 rounded-lg bg-primary/10">
                                                        <ScrollText className="h-4 w-4 text-primary" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-xs text-default-500 uppercase tracking-wide font-medium">
                                                            Course Description
                                                        </p>
                                                        <p className="text-sm text-foreground leading-relaxed mt-1">
                                                            {pasco.courseDescription}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </CardBody>
                                    </Card>

                                    {/* Academic Details */}
                                    <Card className="shadow-none border border-border rounded hover:shadow-custom transition-shadow">
                                        <CardHeader className="border-b border-divider pb-3">
                                            <h3 className="text-sm font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                                Academic Details
                                            </h3>
                                        </CardHeader>
                                        <CardBody className="space-y-3">
                                            <div className="flex items-start gap-3">
                                                <div className="p-2 rounded-lg bg-blue-500/10">
                                                    <Calendar className="h-4 w-4 text-blue-500" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Academic Year</p>
                                                    <p className="text-sm font-semibold text-foreground">{pasco.academicYear}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-3">
                                                <div className="p-2 rounded-lg bg-purple-500/10">
                                                    <Hash className="h-4 w-4 text-purple-500" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Semester</p>
                                                    <p className="text-sm font-semibold text-foreground">
                                                        {pasco.semester === 1 ? "1st Semester" : pasco.semester === 2 ? "2nd Semester" : pasco.semester === 3 ? "3rd Semester" : `${pasco.semester} Semester`}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-3">
                                                <div className="p-2 rounded-lg bg-green-500/10">
                                                    <Calendar className="h-4 w-4 text-green-500" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Created</p>
                                                    <p className="text-sm font-semibold text-foreground">
                                                        {formatDateHuman(pasco.createdAt)}
                                                    </p>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>
                            </div>
                        </ModalBody>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
}
